import React from "react"
import { Dispatch } from "redux"

import { DESC } from "const"
import { QidModel } from "server/models"
import { sort } from "client/store/utils"
import {
    ResultDatapointListType,
    SilModelDevicesQidExpanded,
    CalculateQidResultProps,
    CalculateQidResultData,
    setSilModelDevicesQidExpanded,
} from "client/store"
import {
    TableRow,
    TableCell,
    TableCellContentWrapper,
    TableCellContent,
    Empty,
    NoDatapoints,
} from "client/components"

import {
    TableCellLined,
    RowCollapseControl,
} from "client/pages/qid/product/models/list/styled"

import { ColumnsType, SelectDeviceType, SelectDevice } from "../columns"
import Datapoint from "../nestedDatapoints"
import SafetyFunctionRows from "./safetyFunctionRows"

import { paddingStyle } from "./index"

const ModelRows = (
    columns: ColumnsType,
    models: readonly QidModel[],
    expanded: SilModelDevicesQidExpanded,
    datapointList: ResultDatapointListType,
    productId: number,
    baseCalculationProps: CalculateQidResultProps,
    selectProductProps: SelectDeviceType,
    dispatch: Dispatch,
) => {
    const toggleModel = (modelId: number) => {
        const expandedModels = expanded.modelIds.slice()
        if (expandedModels.includes(modelId)) {
            dispatch(setSilModelDevicesQidExpanded({
                modelIds: expandedModels.filter((id) => id !== modelId),
            }))
        } else {
            expandedModels.push(modelId)
            dispatch(setSilModelDevicesQidExpanded({ modelIds: expandedModels }))
        }
    }

    let rows: React.ReactNode[] = []

    if (!models.length) {
        rows.push(
            <TableRow key={`emptyProduct_${productId}`} size="sm">
                <TableCellLined lineHalfCross />
                <TableCell colSpan={100}>
                    <TableCellContentWrapper>
                        <Empty />
                    </TableCellContentWrapper>
                </TableCell>
            </TableRow>,
        )
    }

    models.forEach((model, modelIndex) => {
        const modelExpanded = expanded.modelIds.includes(model.id)
        const lastModelRow = models.length - 1 === modelIndex
        const modelAvg = CalculateQidResultData({
            ...baseCalculationProps,
            modelId: model.id,
        })

        const selectModelProps: SelectDeviceType = {
            ...selectProductProps,
            model,
            dataAvg: modelAvg,
        }

        rows.push(
            <TableRow
                key={`model_${model.id}`}
                size="sm"
                onClick={() => SelectDevice(selectModelProps)}
            >
                <TableCellLined
                    // all rows have "cross line", and the last row has only "half line"
                    lineCross={!lastModelRow}
                    lineHalfCross={lastModelRow}
                />
                <TableCell colSpan={3}>
                    <TableCellContentWrapper style={paddingStyle}>
                        <TableCellContent>
                            <RowCollapseControl
                                collapsed={!modelExpanded}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    toggleModel(model.id)
                                }}
                            />
                            {model.name}
                        </TableCellContent>
                    </TableCellContentWrapper>
                </TableCell>
                {
                    columns.commonColumns.map((column) => (
                        <TableCell
                            key={column.field}
                            cellWidth={column.cellWidth}
                            cellMinWidth={column.cellMinWidth}
                            cellMaxWidth={column.cellMaxWidth}
                        >
                            <TableCellContentWrapper>
                                <TableCellContent>
                                    {
                                        column.formatter
                                            ? column.formatter(model)
                                            : model[column.field as keyof QidModel]
                                    }
                                </TableCellContent>
                            </TableCellContentWrapper>
                        </TableCell>
                    ))
                }
                {
                    columns.resultColumns.map((column) => (
                        <TableCell
                            key={column.field}
                            cellWidth={column.cellWidth}
                            cellMinWidth={column.cellMinWidth}
                            cellMaxWidth={column.cellMaxWidth}
                        >
                            <TableCellContentWrapper>
                                <TableCellContent>
                                    { column.formatter(modelAvg) }
                                </TableCellContent>
                            </TableCellContentWrapper>
                        </TableCell>
                    ))
                }
            </TableRow>,
        )

        if (modelExpanded) {
            // order datapoints by "createdAt" DESC - show the newest on top
            const modelKey = `model_${model.id}_safetyFunction_null_option_null`
            const modelDatapoints = datapointList[modelKey] && datapointList[modelKey].datapoints
                ? datapointList[modelKey].datapoints.slice().sort((a, b) => sort(a, b, "createdAt", DESC))
                : []

            rows.push(
                <TableRow key={`model_${model.id}_datapoints`} size="fit">
                    <TableCellLined lineThrough={!lastModelRow} />
                    <TableCellLined lineThrough />
                    <TableCell colSpan={100}>
                        <TableCellContentWrapper columnDirection>
                            {
                                modelDatapoints.length === 0
                                && <NoDatapoints />
                            }
                            {
                                modelDatapoints.map((datapoint) => {
                                    const datapointAvg = CalculateQidResultData({
                                        ...baseCalculationProps,
                                        modelId: model.id,
                                        datapointId: datapoint.id,
                                    })

                                    const selectModelDatapointProps: SelectDeviceType = {
                                        ...selectModelProps,
                                        method: null,
                                        datapoint,
                                        dataAvg: datapointAvg,
                                    }

                                    return (
                                        <Datapoint
                                            key={datapoint.id}
                                            datapoint={datapoint}
                                            datapointAvg={datapointAvg}
                                            onClick={() => SelectDevice(selectModelDatapointProps)}
                                        />
                                    )
                                })
                            }
                        </TableCellContentWrapper>
                    </TableCell>
                </TableRow>,
            )

            const functionRows = SafetyFunctionRows(
                columns,
                model.safetyFunctions,
                expanded,
                model.id,
                lastModelRow,
                baseCalculationProps,
                selectModelProps,
                datapointList,
                dispatch,
            )
            rows = rows.concat(functionRows)
        }
    })

    return rows
}
export default ModelRows
